/**
 * Notification Center Component
 * In-app notifications with tabs, settings, and browser push support
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, Settings,
  Brain, AlertTriangle, Clock, Award, Info, X,
  ChevronRight, Volume2, VolumeX, Mail, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification type icons and colors
const NOTIFICATION_STYLES = {
  insight_ready: { 
    icon: Brain, 
    color: 'text-violet-400', 
    bg: 'bg-violet-500/20',
    label: 'Insights'
  },
  health_alert: { 
    icon: AlertTriangle, 
    color: 'text-red-400', 
    bg: 'bg-red-500/20',
    label: 'Alerts'
  },
  reminder: { 
    icon: Clock, 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/20',
    label: 'Reminders'
  },
  achievement: { 
    icon: Award, 
    color: 'text-yellow-400', 
    bg: 'bg-yellow-500/20',
    label: 'Achievements'
  },
  system: { 
    icon: Info, 
    color: 'text-gray-400', 
    bg: 'bg-gray-500/20',
    label: 'System'
  }
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'insight_ready', label: 'Insights' },
  { id: 'health_alert', label: 'Alerts' },
  { id: 'reminder', label: 'Reminders' }
];

// Notification Bell with Badge
export const NotificationBell = ({ token, onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  return (
    <button
      onClick={onClick}
      className="relative p-2 text-gray-400 hover:text-white transition-colors"
      data-testid="notification-bell"
    >
      <Bell className="w-6 h-6" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

// Full Notification Center
const NotificationCenter = ({ token, isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchNotifications();
      fetchUnreadCounts();
      fetchPreferences();
    }
  }, [isOpen, token, activeTab]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const typeParam = activeTab !== 'all' ? `&notification_type=${activeTab}` : '';
      const res = await fetch(`${API_URL}/api/sankat-mochan/notifications?limit=50${typeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/sankat-mochan/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      fetchUnreadCounts();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const formData = new FormData();
      if (activeTab !== 'all') {
        formData.append('notification_type', activeTab);
      }
      
      await fetch(`${API_URL}/api/sankat-mochan/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      fetchUnreadCounts();
      toast.success('All marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/sankat-mochan/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchUnreadCounts();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const clearAll = async () => {
    if (!confirm('Are you sure you want to clear all notifications?')) return;
    
    try {
      const params = activeTab !== 'all' ? `?notification_type=${activeTab}` : '';
      await fetch(`${API_URL}/api/sankat-mochan/notifications${params}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications([]);
      fetchUnreadCounts();
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  const updatePreferences = async (updates) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/notifications/preferences`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreferences(data);
        toast.success('Preferences updated');
      }
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser does not support notifications');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      toast.success('Push notifications enabled!');
      // Register service worker and subscription here
      updatePreferences({ push_enabled: true });
    } else {
      toast.error('Push notifications denied');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-end p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl mt-16 mr-4">
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-violet-400" />
              Notifications
              {unreadCounts.total > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCounts.total}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg ${showSettings ? 'bg-violet-500 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <Settings className="w-5 h-5" />
              </button>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          {!showSettings && (
            <div className="flex gap-1 overflow-x-auto">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? 'bg-violet-500 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  {tab.label}
                  {unreadCounts[tab.id] > 0 && (
                    <span className="ml-1 text-xs">({unreadCounts[tab.id]})</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {showSettings ? (
            <NotificationSettings 
              preferences={preferences}
              onUpdate={updatePreferences}
              onRequestPush={requestPushPermission}
            />
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <BellOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {notifications.map(notification => {
                const style = NOTIFICATION_STYLES[notification.type] || NOTIFICATION_STYLES.system;
                const Icon = style.icon;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-700/50 transition-colors ${
                      !notification.is_read ? 'bg-slate-700/30' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-lg ${style.bg}`}>
                        <Icon className={`w-5 h-5 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`font-medium ${notification.is_read ? 'text-gray-300' : 'text-white'}`}>
                            {notification.title}
                          </h3>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          {!notification.is_read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" /> Mark read
                            </button>
                          )}
                          {notification.action_url && (
                            <a
                              href={notification.action_url}
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              View <ChevronRight className="w-3 h-3" />
                            </a>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1 ml-auto"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!showSettings && notifications.length > 0 && (
          <div className="p-3 border-t border-slate-700 flex justify-between">
            <button
              onClick={markAllAsRead}
              className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1"
            >
              <CheckCheck className="w-4 h-4" /> Mark all read
            </button>
            <button
              onClick={clearAll}
              className="text-sm text-gray-500 hover:text-red-400 flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettings = ({ preferences, onUpdate, onRequestPush }) => {
  if (!preferences) return null;

  const toggleCategory = (category, field, value) => {
    const newCategories = {
      ...preferences.categories,
      [category]: {
        ...preferences.categories[category],
        [field]: value
      }
    };
    onUpdate({ categories: newCategories });
  };

  const toggleQuietHours = (enabled) => {
    onUpdate({
      quiet_hours: {
        ...preferences.quiet_hours,
        enabled
      }
    });
  };

  return (
    <div className="p-4 space-y-6">
      {/* Master Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium">Notifications</h3>
          <p className="text-sm text-gray-400">Enable or disable all notifications</p>
        </div>
        <ToggleSwitch
          enabled={preferences.enabled}
          onChange={(v) => onUpdate({ enabled: v })}
        />
      </div>

      {/* Push Notifications */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Smartphone className="w-5 h-5 text-gray-400" />
          <div>
            <h3 className="text-white font-medium">Push Notifications</h3>
            <p className="text-sm text-gray-400">Browser push alerts</p>
          </div>
        </div>
        <button
          onClick={onRequestPush}
          className="px-3 py-1 bg-violet-500/20 text-violet-400 rounded-lg text-sm hover:bg-violet-500/30"
        >
          {preferences.push_enabled ? 'Enabled' : 'Enable'}
        </button>
      </div>

      {/* Category Settings */}
      <div>
        <h3 className="text-white font-medium mb-3">Notification Types</h3>
        <div className="space-y-3">
          {Object.entries(NOTIFICATION_STYLES).map(([type, style]) => {
            const Icon = style.icon;
            const catPrefs = preferences.categories?.[type] || {};
            
            return (
              <div key={type} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${style.color}`} />
                  <span className="text-gray-300">{style.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleCategory(type, 'push', !catPrefs.push)}
                    className={`p-1.5 rounded ${catPrefs.push ? 'bg-violet-500/20 text-violet-400' : 'text-gray-500'}`}
                    title="Push notifications"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                  <ToggleSwitch
                    enabled={catPrefs.enabled !== false}
                    onChange={(v) => toggleCategory(type, 'enabled', v)}
                    small
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
        <div>
          <h3 className="text-white font-medium">Quiet Hours</h3>
          <p className="text-sm text-gray-400">
            {preferences.quiet_hours?.start} - {preferences.quiet_hours?.end}
          </p>
        </div>
        <ToggleSwitch
          enabled={preferences.quiet_hours?.enabled}
          onChange={toggleQuietHours}
        />
      </div>

      {/* Reminder Settings */}
      <div>
        <h3 className="text-white font-medium mb-3">Reminders</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Health check reminder after</span>
            <select
              value={preferences.reminder_settings?.health_check_reminder_days || 3}
              onChange={(e) => onUpdate({
                reminder_settings: {
                  ...preferences.reminder_settings,
                  health_check_reminder_days: parseInt(e.target.value)
                }
              })}
              className="bg-slate-700 text-white rounded px-2 py-1"
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, small = false }) => {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative rounded-full transition-colors ${
        enabled ? 'bg-violet-500' : 'bg-slate-600'
      } ${small ? 'w-10 h-5' : 'w-12 h-6'}`}
    >
      <span
        className={`absolute top-0.5 bg-white rounded-full transition-transform ${
          small ? 'w-4 h-4' : 'w-5 h-5'
        } ${enabled ? (small ? 'translate-x-5' : 'translate-x-6') : 'translate-x-0.5'}`}
      />
    </button>
  );
};

export default NotificationCenter;
